/* eslint-disable react/no-unknown-property */
/**
 * Fighter3DCard.jsx — WebGL 3D fighter card (Feb 2026 owner directive)
 *
 * Renders a fighter as a procedural 3D scene using react-three-fiber.
 * When owner drops a real character GLB/FBX model into `fighter.model_url`,
 * the card auto-swaps to render the real model (see <FighterModel> below).
 * Until then, each hero is represented as a procedural 3D form tinted with
 * their accent color, orbiting a floating icon billboard and a spinning
 * ring of their tagline text.
 *
 * Perf notes:
 * - dpr clamped to 2 (retina cap)
 * - frameloop="demand" while idle (only re-render on interaction)
 * - suspense-safe: <FighterModel> lazily loads GLBs; <ProceduralAvatar>
 *   is a fallback for heroes without a model asset
 */
import { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import {
  OrbitControls,
  Environment,
  ContactShadows,
  useGLTF,
} from "@react-three/drei";
import * as THREE from "three";

// Accent-color hex map — matches ACCENT_MAP in CrossoverShowdown.jsx.
const ACCENT_HEX = {
  amber:    "#F59E0B",
  emerald:  "#10B981",
  violet:   "#8B5CF6",
  rose:     "#F43F5E",
  cyan:     "#06B6D4",
  fuchsia:  "#D946EF",
  orange:   "#F97316",
  slate:    "#64748B",
  sky:      "#0EA5E9",
  yellow:   "#EAB308",
  lime:     "#84CC16",
  red:      "#EF4444",
  purple:   "#A855F7",
  indigo:   "#6366F1",
  teal:     "#14B8A6",
  gray:     "#9CA3AF",
  blue:     "#3B82F6",
  pink:     "#EC4899",
};

/**
 * <ProceduralAvatar> — the default 3D representation.
 * A geometry combo (torus knot core + orbiting satellites) tinted by
 * the hero's accent color. Rotates continuously.
 */
function ProceduralAvatar({ colorHex, seed = 0 }) {
  const coreRef = useRef();
  const orbitRef = useRef();

  useFrame((_, dt) => {
    if (coreRef.current) coreRef.current.rotation.y += dt * 0.35;
    if (coreRef.current) coreRef.current.rotation.x = Math.sin(_.clock.elapsedTime * 0.4) * 0.15;
    if (orbitRef.current) orbitRef.current.rotation.y -= dt * 0.6;
  });

  // Derive satellite positions from a seed so each hero looks distinct.
  const satellites = useMemo(() => {
    const arr = [];
    const n = 4;
    for (let i = 0; i < n; i++) {
      const angle = ((i + seed * 0.7) / n) * Math.PI * 2;
      arr.push({
        pos: [Math.cos(angle) * 1.8, Math.sin(angle * 1.7) * 0.4, Math.sin(angle) * 1.8],
        size: 0.14 + ((i + seed) % 3) * 0.03,
      });
    }
    return arr;
  }, [seed]);

  return (
    <group>
      {/* Core knot — hero's signature form */}
      <mesh ref={coreRef} castShadow>
        <torusKnotGeometry args={[0.85, 0.28, 128, 24, 2, 3]} />
        <meshStandardMaterial
          color={colorHex}
          metalness={0.6}
          roughness={0.25}
          emissive={colorHex}
          emissiveIntensity={0.15}
        />
      </mesh>

      {/* Inner glowing sphere */}
      <mesh>
        <sphereGeometry args={[0.35, 32, 32]} />
        <meshBasicMaterial color={colorHex} transparent opacity={0.35} />
      </mesh>

      {/* Orbiting satellites — visually distinct per hero via seed */}
      <group ref={orbitRef}>
        {satellites.map((s, i) => (
          <mesh key={i} position={s.pos} castShadow>
            <icosahedronGeometry args={[s.size, 0]} />
            <meshStandardMaterial
              color={colorHex}
              metalness={0.8}
              roughness={0.15}
              emissive={colorHex}
              emissiveIntensity={0.4}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
}

/**
 * <FighterModel> — real character GLB/FBX loader.
 * Currently a no-op wrapper because no character GLB assets are dropped
 * yet. When owner adds `model_url` to a fighter config, this component
 * loads it via useGLTF and renders the scene node. Fallback path keeps
 * the procedural avatar so nothing ever breaks.
 */
function FighterModel({ modelUrl, colorHex }) {
  const gltf = useGLTF(modelUrl);
  // Tint mesh materials with the hero's accent as a subtle rim emissive.
  // Runs once on scene ready.
  useMemo(() => {
    if (!gltf?.scene) return;
    gltf.scene.traverse((obj) => {
      if (obj.isMesh && obj.material) {
        obj.material.emissive = new THREE.Color(colorHex);
        obj.material.emissiveIntensity = 0.08;
      }
    });
  }, [gltf, colorHex]);
  if (!gltf?.scene) return null;
  return <primitive object={gltf.scene} dispose={null} />;
}

/**
 * <SpinningRingText> — REMOVED. Was using drei's <Text> which requires font
 * loading. Replaced by HTML label overlays outside the Canvas (see main
 * component below). Keeping stub for git-blame clarity.
 */

/**
 * <Fighter3DCard> — main export.
 * Renders the R3F Canvas scene for one fighter.
 * Height controlled by the parent wrapper (needs a height so the canvas fills).
 */
export default function Fighter3DCard({ fighter, height = 380 }) {
  const colorHex = ACCENT_HEX[fighter.accent] || "#A855F7";
  const seed = (fighter.id || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 9;
  const hasModel = !!fighter.model_url;
  const tagLabel = (fighter.tagline || fighter.tag || fighter.archetype || fighter.name || "").slice(0, 96);

  return (
    <div
      style={{ height, width: "100%" }}
      className="rounded-xl overflow-hidden border-2 border-neutral-800 bg-gradient-to-br from-black via-neutral-950 to-black relative"
      data-testid={`showdown-3d-card-${fighter.id}`}
    >
      {/* HTML label overlays — avoid drei's <Text> font-loading path */}
      <div
        className="absolute top-2 left-2 z-10 text-2xl leading-none pointer-events-none"
        style={{ textShadow: "0 0 8px #000, 0 2px 4px #000", color: colorHex }}
      >
        {fighter.icon || "★"}
      </div>
      <div className="absolute top-2 right-2 z-10 text-[10px] uppercase tracking-widest font-bold text-white pointer-events-none"
           style={{ textShadow: "0 1px 3px #000" }}>
        {fighter.name}
      </div>
      <div className="absolute bottom-2 left-2 right-2 z-10 text-[9px] italic text-neutral-300 pointer-events-none truncate text-center"
           style={{ textShadow: "0 1px 3px #000" }}>
        {tagLabel}
      </div>

      <Canvas
        camera={{ position: [0, 1.1, 4.8], fov: 45 }}
        dpr={[1, 2]}
        gl={{ antialias: true, alpha: true }}
        shadows
      >
        {/* Lighting */}
        <ambientLight intensity={0.35} />
        <directionalLight position={[3, 5, 4]} intensity={0.9} castShadow />
        <pointLight position={[-3, 2, -3]} intensity={0.4} color={colorHex} />

        {/* Environment — cheap studio HDRI */}
        <Suspense fallback={null}>
          <Environment preset="city" />
        </Suspense>

        {/* Fighter representation */}
        <Suspense fallback={<ProceduralAvatar colorHex={colorHex} seed={seed} />}>
          {hasModel ? (
            <FighterModel modelUrl={fighter.model_url} colorHex={colorHex} />
          ) : (
            <ProceduralAvatar colorHex={colorHex} seed={seed} />
          )}
        </Suspense>

        {/* Contact shadow beneath */}
        <ContactShadows
          position={[0, -1.45, 0]}
          opacity={0.55}
          scale={5.5}
          blur={2.5}
          far={2.2}
        />

        {/* User interaction */}
        <OrbitControls
          enablePan={false}
          enableZoom={false}
          autoRotate={!hasModel}
          autoRotateSpeed={0.6}
          minPolarAngle={Math.PI / 3.5}
          maxPolarAngle={Math.PI / 1.9}
        />
      </Canvas>
    </div>
  );
}
